import { useState, useEffect, useCallback } from 'react';
import { useEnvironment } from '../context/EnvironmentContext';
import { cronAPI } from '../services/api';
import cronstrue from 'cronstrue';
import { CronExpressionParser } from 'cron-parser';
import JsonViewer from '../components/JsonViewer';
import {
  ClockIcon,
  PlayIcon,
  PauseIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ChartBarIcon,
  ListBulletIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

// Schedule frequency options
const FREQUENCY_OPTIONS = [
  { value: 'minutes', label: 'Every X minutes' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom (advanced)' },
];

// Common day of month presets for quick selection
const MONTHLY_PRESETS = [
  { value: '1', label: '1st of the month' },
  { value: 'last', label: 'Last day of month' },
  { value: 'custom', label: 'Custom days...' },
];

const MINUTE_INTERVALS = [
  { value: '1', label: 'Every minute' },
  { value: '5', label: 'Every 5 minutes' },
  { value: '10', label: 'Every 10 minutes' },
  { value: '15', label: 'Every 15 minutes' },
  { value: '30', label: 'Every 30 minutes' },
];

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sunday', short: 'Sun' },
  { value: '1', label: 'Monday', short: 'Mon' },
  { value: '2', label: 'Tuesday', short: 'Tue' },
  { value: '3', label: 'Wednesday', short: 'Wed' },
  { value: '4', label: 'Thursday', short: 'Thu' },
  { value: '5', label: 'Friday', short: 'Fri' },
  { value: '6', label: 'Saturday', short: 'Sat' },
];

// Common weekly schedule presets
const WEEKLY_PRESETS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: 'custom', label: 'Custom days...' },
];

// Parse cron expression to determine frequency type
function parseCronToFrequency(cronExpr) {
  const parts = cronExpr.split(' ');
  if (parts.length !== 5) return { frequency: 'custom', cronExpression: cronExpr };

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Every X minutes: */X * * * *
  if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return { frequency: 'minutes', minuteInterval: minute.substring(2) };
  }

  // Hourly: X * * * * (minute is specific, hour is *)
  if (!minute.includes('*') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return { frequency: 'hourly', minute };
  }

  // Daily: X X * * * (minute and hour specific, others *)
  if (!minute.includes('*') && !hour.includes('*') && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return { frequency: 'daily', minute, hour };
  }

  // Weekly: X X * * X (minute, hour, dayOfWeek specific - can be single, comma-separated, or range)
  if (!minute.includes('*') && !hour.includes('*') && dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    // Check if it's a single day (0-6) or multiple days
    let weeklyPreset = 'custom';
    let customWeekDays = [];

    // Single day presets (0=Sun, 1=Mon, ..., 6=Sat)
    const singleDays = ['0', '1', '2', '3', '4', '5', '6'];
    if (singleDays.includes(dayOfWeek)) {
      weeklyPreset = dayOfWeek;
    } else {
      // Multiple days - parse the comma-separated list or range
      weeklyPreset = 'custom';
      if (dayOfWeek.includes('-')) {
        // Expand range (e.g., "1-5" becomes ["1","2","3","4","5"])
        const [start, end] = dayOfWeek.split('-').map(Number);
        for (let i = start; i <= end; i++) {
          customWeekDays.push(i.toString());
        }
      } else {
        customWeekDays = dayOfWeek.split(',').map(d => d.trim());
      }
    }

    return { frequency: 'weekly', minute, hour, dayOfWeek, weeklyPreset, customWeekDays };
  }

  // Monthly: X X X * * (minute, hour, dayOfMonth specific - can be single, comma-separated, or L for last)
  if (!minute.includes('*') && !hour.includes('*') && dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    // Check if it's a known preset or custom
    let monthlyPreset = 'custom';
    let customDays = [];

    if (dayOfMonth === 'L') {
      monthlyPreset = 'last';
    } else if (dayOfMonth === '1') {
      monthlyPreset = '1';
    } else {
      // Custom days - parse the comma-separated list
      monthlyPreset = 'custom';
      customDays = dayOfMonth.split(',').map(d => d.trim());
    }

    return { frequency: 'monthly', minute, hour, dayOfMonth, monthlyPreset, customDays };
  }

  return { frequency: 'custom', cronExpression: cronExpr };
}

// Build cron expression from frequency settings
function buildCronExpression(settings) {
  switch (settings.frequency) {
    case 'minutes':
      return `*/${settings.minuteInterval || '15'} * * * *`;
    case 'hourly':
      return `${settings.minute || '0'} * * * *`;
    case 'daily':
      return `${settings.minute || '0'} ${settings.hour || '0'} * * *`;
    case 'weekly': {
      const minute = settings.minute || '0';
      const hour = settings.hour || '0';
      let dayPart = '1';

      // Handle weekly preset options
      if (settings.weeklyPreset === 'custom' && settings.customWeekDays?.length > 0) {
        // Sort days numerically and join with comma
        dayPart = [...settings.customWeekDays].sort((a, b) => parseInt(a) - parseInt(b)).join(',');
      } else if (settings.weeklyPreset && settings.weeklyPreset !== 'custom') {
        // Use the preset value directly (e.g., '1', '1-5', '0,6', '1,3,5')
        dayPart = settings.weeklyPreset;
      } else if (settings.dayOfWeek) {
        // Fallback to dayOfWeek for backwards compatibility
        dayPart = settings.dayOfWeek;
      }

      return `${minute} ${hour} * * ${dayPart}`;
    }
    case 'monthly': {
      const minute = settings.minute || '0';
      const hour = settings.hour || '0';
      let dayPart = '1';

      // Handle monthly preset options
      if (settings.monthlyPreset === 'last') {
        dayPart = 'L';
      } else if (settings.monthlyPreset === 'custom' && settings.customDays?.length > 0) {
        // Sort days numerically and join with comma
        dayPart = [...settings.customDays].sort((a, b) => parseInt(a) - parseInt(b)).join(',');
      } else if (settings.monthlyPreset && settings.monthlyPreset !== 'custom') {
        // Use the preset value directly (e.g., '1', '15', '1,15')
        dayPart = settings.monthlyPreset;
      } else if (settings.dayOfMonth) {
        // Fallback to dayOfMonth for backwards compatibility
        dayPart = settings.dayOfMonth;
      }

      return `${minute} ${hour} ${dayPart} * *`;
    }
    case 'custom':
      return settings.cronExpression || '0 * * * *';
    default:
      return '0 * * * *';
  }
}

// Schedule Picker Component
function SchedulePicker({ value, onChange }) {
  const parsed = parseCronToFrequency(value);
  const [frequency, setFrequency] = useState(parsed.frequency);
  const [minuteInterval, setMinuteInterval] = useState(parsed.minuteInterval || '15');
  const [minute, setMinute] = useState(parsed.minute || '0');
  const [hour, setHour] = useState(parsed.hour || '9');
  const [dayOfWeek, setDayOfWeek] = useState(parsed.dayOfWeek || '1');
  const [weeklyPreset, setWeeklyPreset] = useState(parsed.weeklyPreset || '1');
  const [customWeekDays, setCustomWeekDays] = useState(parsed.customWeekDays || []);
  const [dayOfMonth, setDayOfMonth] = useState(parsed.dayOfMonth || '1');
  const [monthlyPreset, setMonthlyPreset] = useState(parsed.monthlyPreset || '1');
  const [customDays, setCustomDays] = useState(parsed.customDays || []);
  const [customCron, setCustomCron] = useState(parsed.cronExpression || value);

  // Update parent when settings change
  useEffect(() => {
    const newCron = buildCronExpression({
      frequency,
      minuteInterval,
      minute,
      hour,
      dayOfWeek,
      weeklyPreset,
      customWeekDays,
      dayOfMonth,
      monthlyPreset,
      customDays,
      cronExpression: customCron,
    });
    onChange(newCron);
  }, [frequency, minuteInterval, minute, hour, dayOfWeek, weeklyPreset, customWeekDays, dayOfMonth, monthlyPreset, customDays, customCron, onChange]);

  // Toggle a day in the custom week days selection
  const toggleCustomWeekDay = (day) => {
    setCustomWeekDays(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      } else {
        return [...prev, day];
      }
    });
  };

  // Toggle a day in the custom month days selection
  const toggleCustomDay = (day) => {
    setCustomDays(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      } else {
        return [...prev, day];
      }
    });
  };

  const previewText = formatCronExpression(buildCronExpression({
    frequency,
    minuteInterval,
    minute,
    hour,
    dayOfWeek,
    weeklyPreset,
    customWeekDays,
    dayOfMonth,
    monthlyPreset,
    customDays,
    cronExpression: customCron,
  }));

  return (
    <div className="space-y-4">
      {/* Frequency selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
        <select
          value={frequency}
          onChange={(e) => setFrequency(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {FREQUENCY_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Frequency-specific options */}
      {frequency === 'minutes' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Run every</label>
          <select
            value={minuteInterval}
            onChange={(e) => setMinuteInterval(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {MINUTE_INTERVALS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {frequency === 'hourly' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">At minute</label>
          <select
            value={minute}
            onChange={(e) => setMinute(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => (
              <option key={m} value={m.toString()}>:{m.toString().padStart(2, '0')}</option>
            ))}
          </select>
        </div>
      )}

      {(frequency === 'daily' || frequency === 'weekly' || frequency === 'monthly') && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hour</label>
            <select
              value={hour}
              onChange={(e) => setHour(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i.toString()}>
                  {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Minute</label>
            <select
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map(m => (
                <option key={m} value={m.toString()}>:{m.toString().padStart(2, '0')}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {frequency === 'weekly' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Run on</label>
            <select
              value={weeklyPreset}
              onChange={(e) => {
                setWeeklyPreset(e.target.value);
                // Reset custom days when switching away from custom
                if (e.target.value !== 'custom') {
                  setCustomWeekDays([]);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {WEEKLY_PRESETS.map(preset => (
                <option key={preset.value} value={preset.value}>{preset.label}</option>
              ))}
            </select>
          </div>

          {/* Custom days picker - show when "Custom days..." is selected */}
          {weeklyPreset === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select days of the week</label>
              <div className="flex gap-1">
                {DAYS_OF_WEEK.map(day => {
                  const isSelected = customWeekDays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleCustomWeekDay(day.value)}
                      className={`
                        flex-1 py-2 text-xs font-medium rounded-md transition-colors
                        ${isSelected
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }
                      `}
                    >
                      {day.short}
                    </button>
                  );
                })}
              </div>
              {customWeekDays.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">
                  Click on days to select when the job should run
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {frequency === 'monthly' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Run on</label>
            <select
              value={monthlyPreset}
              onChange={(e) => {
                setMonthlyPreset(e.target.value);
                // Reset custom days when switching away from custom
                if (e.target.value !== 'custom') {
                  setCustomDays([]);
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {MONTHLY_PRESETS.map(preset => (
                <option key={preset.value} value={preset.value}>{preset.label}</option>
              ))}
            </select>
          </div>

          {/* Custom days picker - show when "Custom days..." is selected */}
          {monthlyPreset === 'custom' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select days of the month</label>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 31 }, (_, i) => {
                  const day = (i + 1).toString();
                  const isSelected = customDays.includes(day);
                  const isEdgeDay = i >= 28; // Days 29, 30, 31
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleCustomDay(day)}
                      className={`
                        w-8 h-8 text-xs font-medium rounded-md transition-colors
                        ${isSelected
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : isEdgeDay
                            ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }
                      `}
                      title={isEdgeDay ? `Day ${day} doesn't exist in all months` : ''}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
              {customDays.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Selected: {customDays.sort((a, b) => parseInt(a) - parseInt(b)).join(', ')}
                </p>
              )}
              {customDays.length === 0 && (
                <p className="text-xs text-amber-600 mt-2">
                  Click on days to select when the job should run
                </p>
              )}
              {/* Edge case warning for days 29-31 */}
              {customDays.some(d => parseInt(d) >= 29) && (
                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                  <strong>Note:</strong> Days 29-31 don't exist in all months.
                  The job will skip months without those days (e.g., Feb has 28-29, Apr/Jun/Sep/Nov have 30).
                  Use "Last day of month" for consistent end-of-month runs.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {frequency === 'custom' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cron expression</label>
          <input
            type="text"
            value={customCron}
            onChange={(e) => setCustomCron(e.target.value)}
            placeholder="* * * * *"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">Format: minute hour day-of-month month day-of-week</p>
        </div>
      )}

      {/* Preview */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-2">
          <ClockIcon className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">{previewText}</span>
        </div>
        <code className="text-xs text-blue-600 font-mono mt-1 block">
          {buildCronExpression({ frequency, minuteInterval, minute, hour, dayOfWeek, weeklyPreset, customWeekDays, dayOfMonth, monthlyPreset, customDays, cronExpression: customCron })}
        </code>
      </div>
    </div>
  );
}

// Edit Job Modal Component
function EditJobModal({ job, onSave, onClose, saving }) {
  const [cronExpression, setCronExpression] = useState(job.cron_expression);
  const [enabled, setEnabled] = useState(job.enabled);
  const [retryEnabled, setRetryEnabled] = useState(job.retry_enabled);
  const [maxRetries, setMaxRetries] = useState(job.max_retries);
  const [timeoutSeconds, setTimeoutSeconds] = useState(job.timeout_seconds);

  const handleSave = () => {
    onSave({
      cron_expression: cronExpression,
      enabled,
      retry_enabled: retryEnabled,
      max_retries: parseInt(maxRetries),
      timeout_seconds: parseInt(timeoutSeconds),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit Job Configuration</h2>
            <p className="text-sm text-gray-500">{job.display_name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-6">
          {/* Enabled toggle */}
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Job Enabled</label>
              <p className="text-xs text-gray-500">When disabled, the job will not run on schedule</p>
            </div>
            <button
              onClick={() => setEnabled(!enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                enabled ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Schedule picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Schedule</label>
            <SchedulePicker value={cronExpression} onChange={setCronExpression} />
          </div>

          {/* Retry settings */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">Retry on Failure</label>
                <p className="text-xs text-gray-500">Automatically retry if the job fails</p>
              </div>
              <button
                onClick={() => setRetryEnabled(!retryEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  retryEnabled ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    retryEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {retryEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Retries</label>
                <select
                  value={maxRetries}
                  onChange={(e) => setMaxRetries(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[1, 2, 3, 5, 10].map(n => (
                    <option key={n} value={n}>{n} {n === 1 ? 'retry' : 'retries'}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Timeout */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timeout</label>
            <select
              value={timeoutSeconds}
              onChange={(e) => setTimeoutSeconds(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="60">1 minute</option>
              <option value="120">2 minutes</option>
              <option value="180">3 minutes</option>
              <option value="300">5 minutes</option>
              <option value="600">10 minutes</option>
              <option value="900">15 minutes</option>
              <option value="1800">30 minutes</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Job will be cancelled if it runs longer than this</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Execution Detail Modal Component
function ExecutionDetailModal({ execution, onClose }) {
  const statusColor = STATUS_COLORS[execution.status] || 'bg-gray-100 text-gray-800';

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatDuration = (ms) => {
    if (!ms && ms !== 0) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    return `${Math.floor(ms / 60000)}m ${((ms % 60000) / 1000).toFixed(0)}s`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Execution Details</h2>
            <p className="text-sm text-gray-500">{execution.display_name || execution.job_key}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Status and Trigger */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${statusColor}`}>
                {execution.status === 'completed' ? (
                  <CheckCircleIcon className="w-4 h-4 mr-1" />
                ) : execution.status === 'failed' ? (
                  <XCircleIcon className="w-4 h-4 mr-1" />
                ) : (
                  <ClockIcon className="w-4 h-4 mr-1" />
                )}
                {execution.status}
              </span>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Triggered By</p>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${
                execution.triggered_by === 'manual'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {execution.triggered_by === 'manual' ? '👤 Manual' : '⏰ Scheduler'}
              </span>
            </div>
          </div>

          {/* Timing */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Timing</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Started:</span>
                <p className="font-medium text-gray-900">{formatDateTime(execution.started_at)}</p>
              </div>
              <div>
                <span className="text-gray-500">Completed:</span>
                <p className="font-medium text-gray-900">{formatDateTime(execution.completed_at)}</p>
              </div>
              <div>
                <span className="text-gray-500">Duration:</span>
                <p className="font-medium text-gray-900">{formatDuration(execution.duration_ms)}</p>
              </div>
              <div>
                <span className="text-gray-500">Execution ID:</span>
                <p className="font-mono text-xs text-gray-600 truncate" title={execution.id}>
                  {execution.id}
                </p>
              </div>
            </div>
          </div>

          {/* Error Message (if failed) */}
          {execution.error_message && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs text-red-600 uppercase tracking-wide mb-1 flex items-center gap-1">
                <ExclamationTriangleIcon className="w-4 h-4" />
                Error Message
              </p>
              <pre className="text-sm text-red-800 whitespace-pre-wrap font-mono bg-red-100 rounded p-2 mt-2 overflow-x-auto">
                {execution.error_message}
              </pre>
            </div>
          )}

          {/* Metadata */}
          {execution.metadata && Object.keys(execution.metadata).length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Execution Results</p>
              <div className="space-y-2">
                {Object.entries(execution.metadata).map(([key, value]) => {
                  // Format the key to be human readable
                  const formattedKey = key
                    .replace(/_/g, ' ')
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())
                    .trim();

                  // Format the value based on type
                  let displayValue;
                  let valueColor = 'text-gray-900';

                  if (value === null || value === undefined) {
                    displayValue = '—';
                    valueColor = 'text-gray-400';
                  } else if (typeof value === 'boolean') {
                    displayValue = value ? '✓ Yes' : '✗ No';
                    valueColor = value ? 'text-green-600' : 'text-gray-500';
                  } else if (typeof value === 'number') {
                    // Format numbers nicely
                    if (key.toLowerCase().includes('ms') || key.toLowerCase().includes('duration')) {
                      displayValue = value < 1000 ? `${value}ms` : `${(value / 1000).toFixed(2)}s`;
                    } else if (key.toLowerCase().includes('count') || key.toLowerCase().includes('total') || key.toLowerCase().includes('processed')) {
                      displayValue = value.toLocaleString();
                      if (value > 0) valueColor = 'text-green-600 font-medium';
                    } else {
                      displayValue = value.toLocaleString();
                    }
                  } else if (Array.isArray(value)) {
                    if (value.length === 0) {
                      displayValue = 'None';
                      valueColor = 'text-gray-400';
                    } else if (value.length <= 5) {
                      displayValue = value.join(', ');
                    } else {
                      displayValue = `${value.slice(0, 5).join(', ')} +${value.length - 5} more`;
                    }
                  } else if (typeof value === 'object') {
                    // For nested objects, show a summary
                    const keys = Object.keys(value);
                    displayValue = `{${keys.length} ${keys.length === 1 ? 'field' : 'fields'}}`;
                    valueColor = 'text-gray-500 font-mono text-xs';
                  } else {
                    displayValue = String(value);
                  }

                  return (
                    <div key={key} className="flex items-start justify-between py-1.5 border-b border-gray-100 last:border-0">
                      <span className="text-sm text-gray-600">{formattedKey}</span>
                      <span className={`text-sm ${valueColor} text-right max-w-[60%]`}>{displayValue}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Job Info */}
          {execution.category && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Job Information</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Category:</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${CATEGORY_COLORS[execution.category] || CATEGORY_COLORS.general}`}>
                  {execution.category}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// Category colors
const CATEGORY_COLORS = {
  subscription: 'bg-blue-50 text-blue-700 border-blue-200',
  events: 'bg-green-50 text-green-700 border-green-200',
  maintenance: 'bg-purple-50 text-purple-700 border-purple-200',
  analytics: 'bg-orange-50 text-orange-700 border-orange-200',
  general: 'bg-gray-50 text-gray-700 border-gray-200',
};

// Status colors
const STATUS_COLORS = {
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  running: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

// Tab configuration
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: ChartBarIcon },
  { id: 'jobs', label: 'All Jobs', icon: ListBulletIcon },
  { id: 'history', label: 'History', icon: ClockIcon },
];

// Helper to format cron expression to plain English
function formatCronExpression(expression) {
  try {
    return cronstrue.toString(expression);
  } catch (error) {
    return expression;
  }
}

// Helper to get next run times
function getNextRunTimes(expression, count = 3) {
  try {
    const interval = CronExpressionParser.parse(expression);
    const times = [];
    for (let i = 0; i < count; i++) {
      times.push(interval.next().toDate());
    }
    return times;
  } catch (error) {
    return [];
  }
}

// Helper to format relative time
function formatRelativeTime(date) {
  if (!date) return 'Never';
  const now = new Date();
  const d = new Date(date);
  const diff = now - d;
  const diffAbs = Math.abs(diff);

  if (diffAbs < 60000) return diff > 0 ? 'Just now' : 'In a moment';
  if (diffAbs < 3600000) {
    const mins = Math.round(diffAbs / 60000);
    return diff > 0 ? `${mins}m ago` : `in ${mins}m`;
  }
  if (diffAbs < 86400000) {
    const hours = Math.round(diffAbs / 3600000);
    return diff > 0 ? `${hours}h ago` : `in ${hours}h`;
  }
  const days = Math.round(diffAbs / 86400000);
  return diff > 0 ? `${days}d ago` : `in ${days}d`;
}

// Job Card Component
function JobCard({ job, onTrigger, onToggle, onExpand, onEdit, isExpanded, loading }) {
  const categoryColor = CATEGORY_COLORS[job.category] || CATEGORY_COLORS.general;
  const statusColor = job.last_run_status ? STATUS_COLORS[job.last_run_status] : 'bg-gray-100 text-gray-600';
  const nextRuns = getNextRunTimes(job.cron_expression, 3);

  return (
    <div className={`card border-2 ${job.enabled ? 'border-gray-200' : 'border-gray-100 opacity-60'}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-gray-900">
              {job.display_name}
            </h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${categoryColor}`}>
              {job.category}
            </span>
            {!job.enabled && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                Disabled
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">
            {job.description}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(job)}
            className="p-2 rounded-lg transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200"
            title="Edit job configuration"
          >
            <PencilSquareIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => onToggle(job.job_key, !job.enabled)}
            className={`p-2 rounded-lg transition-colors ${
              job.enabled
                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            title={job.enabled ? 'Click to disable' : 'Click to enable'}
          >
            {job.enabled ? <PlayIcon className="w-5 h-5" /> : <PauseIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Schedule info */}
      <div className="bg-gray-50 rounded-lg p-3 mb-3">
        <div className="flex items-center gap-2 mb-1">
          <ClockIcon className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            {formatCronExpression(job.cron_expression)}
          </span>
        </div>
        <code className="text-xs text-gray-500 font-mono">
          {job.cron_expression}
        </code>
        {nextRuns.length > 0 && (
          <p className="text-xs text-gray-500 mt-1">
            Next: {formatRelativeTime(nextRuns[0])}
          </p>
        )}
      </div>

      {/* Last run status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Last run:</span>
          {job.last_run_status ? (
            <>
              <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor}`}>
                {job.last_run_status}
              </span>
              <span className="text-xs text-gray-500">
                {formatRelativeTime(job.last_run_at)}
                {job.last_run_duration_ms && ` (${job.last_run_duration_ms}ms)`}
              </span>
            </>
          ) : (
            <span className="text-xs text-gray-400">Never run</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
        <button
          onClick={() => onTrigger(job.job_key)}
          disabled={loading[job.job_key] || !job.enabled}
          className="btn-primary flex-1 text-sm py-2"
        >
          {loading[job.job_key] ? (
            <span className="flex items-center justify-center gap-2">
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
              Running...
            </span>
          ) : (
            <span className="flex items-center justify-center gap-2">
              <PlayIcon className="w-4 h-4" />
              Trigger Now
            </span>
          )}
        </button>
        <button
          onClick={() => onExpand(job.job_key)}
          className="btn-secondary text-sm py-2 px-3"
        >
          {isExpanded ? (
            <ChevronUpIcon className="w-4 h-4" />
          ) : (
            <ChevronDownIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Configuration</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-gray-500">Timezone:</span>{' '}
              <span className="font-medium">{job.timezone}</span>
            </div>
            <div>
              <span className="text-gray-500">Retry:</span>{' '}
              <span className="font-medium">{job.retry_enabled ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="text-gray-500">Max retries:</span>{' '}
              <span className="font-medium">{job.max_retries}</span>
            </div>
            <div>
              <span className="text-gray-500">Timeout:</span>{' '}
              <span className="font-medium">{job.timeout_seconds}s</span>
            </div>
          </div>
          {job.last_error && (
            <div className="mt-3 p-2 bg-red-50 rounded text-xs text-red-700">
              <strong>Last Error:</strong> {job.last_error}
            </div>
          )}
          <p className="mt-2 text-xs text-gray-400">
            Handler: <code>{job.handler_path}</code>
          </p>
        </div>
      )}
    </div>
  );
}

// Dashboard Health Card
function HealthCard({ title, count, color, icon: Icon, items }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`card border-2 ${color}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${color.replace('border-', 'bg-').replace('200', '100')}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">{count}</p>
          </div>
        </div>
        {items && items.length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-gray-400 hover:text-gray-600"
          >
            {expanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
          </button>
        )}
      </div>
      {expanded && items && items.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <ul className="space-y-1">
            {items.map((item, i) => (
              <li key={i} className="text-sm text-gray-600 flex items-center justify-between">
                <span>{item.display_name || item.job_key}</span>
                {item.reason && <span className="text-xs text-gray-400">{item.reason}</span>}
                {item.error && <span className="text-xs text-red-500 truncate max-w-[200px]">{item.error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function CronJobsPage() {
  const { currentEnv } = useEnvironment();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [jobs, setJobs] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedJobs, setExpandedJobs] = useState({});
  const [loading, setLoading] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedJobHistory, setSelectedJobHistory] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Unified history state (dashboard)
  const [unifiedHistory, setUnifiedHistory] = useState([]);
  const [unifiedHistoryTotal, setUnifiedHistoryTotal] = useState(0);
  const [historySearch, setHistorySearch] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const HISTORY_PAGE_SIZE = 20;

  // Modal state
  const [editingJob, setEditingJob] = useState(null);
  const [savingJob, setSavingJob] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);

  // Fetch all jobs
  const fetchJobs = useCallback(async () => {
    try {
      const response = await cronAPI.listJobs();
      setJobs(response.data.jobs || []);
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('[CronJobs] Failed to fetch jobs:', err);
      setError(`Failed to fetch jobs: ${err.response?.data?.message || err.message}`);
    }
  }, []);

  // Fetch dashboard
  const fetchDashboard = useCallback(async () => {
    try {
      const response = await cronAPI.getDashboard();
      setDashboard(response.data.dashboard);
    } catch (err) {
      console.error('[CronJobs] Failed to fetch dashboard:', err);
    }
  }, []);

  // Fetch history for a job
  const fetchJobHistory = useCallback(async (jobKey) => {
    try {
      const response = await cronAPI.getJobHistory(jobKey, { limit: 50 });
      setHistory(response.data.history || []);
      setSelectedJobHistory(jobKey);
    } catch (err) {
      console.error('[CronJobs] Failed to fetch history:', err);
    }
  }, []);

  // Fetch unified history (all jobs)
  const fetchUnifiedHistory = useCallback(async (search = '', status = 'all', page = 1) => {
    setHistoryLoading(true);
    try {
      const params = {
        limit: HISTORY_PAGE_SIZE,
        offset: (page - 1) * HISTORY_PAGE_SIZE,
      };
      if (search) params.search = search;
      if (status && status !== 'all') params.status = status;

      const response = await cronAPI.getUnifiedHistory(params);
      setUnifiedHistory(response.data.history || []);
      setUnifiedHistoryTotal(response.data.total || 0);
    } catch (err) {
      console.error('[CronJobs] Failed to fetch unified history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    setRefreshing(true);
    Promise.all([fetchJobs(), fetchDashboard(), fetchUnifiedHistory()])
      .finally(() => setRefreshing(false));
  }, [fetchJobs, fetchDashboard, fetchUnifiedHistory, currentEnv]);

  // Auto-refresh via polling (15s interval)
  // TODO V2: Replace polling with Server-Sent Events (SSE) or WebSocket
  // for real-time job status updates without constant API calls
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchJobs();
      fetchDashboard();
      fetchUnifiedHistory(historySearch, historyStatusFilter, historyPage);
    }, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchJobs, fetchDashboard, fetchUnifiedHistory, historySearch, historyStatusFilter, historyPage]);

  // Re-fetch unified history when filters change
  useEffect(() => {
    fetchUnifiedHistory(historySearch, historyStatusFilter, historyPage);
  }, [fetchUnifiedHistory, historySearch, historyStatusFilter, historyPage]);

  // Trigger a job
  const handleTrigger = async (jobKey) => {
    setLoading(prev => ({ ...prev, [jobKey]: true }));
    setError(null);
    setResult(null);

    try {
      const response = await cronAPI.triggerJob(jobKey, 'Manual trigger from test harness');
      setResult({
        success: true,
        message: `Job "${response.data.displayName}" executed successfully!`,
        data: response.data,
      });
      // Refresh after trigger
      setTimeout(() => {
        fetchJobs();
        fetchDashboard();
      }, 1000);
    } catch (err) {
      setError(`Failed to trigger ${jobKey}: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [jobKey]: false }));
    }
  };

  // Toggle job enabled/disabled
  const handleToggle = async (jobKey, enabled) => {
    setLoading(prev => ({ ...prev, [`toggle-${jobKey}`]: true }));
    setError(null);

    try {
      await cronAPI.updateJob(jobKey, { enabled });
      setResult({
        success: true,
        message: `Job "${jobKey}" ${enabled ? 'enabled' : 'disabled'} successfully!`,
      });
      fetchJobs();
    } catch (err) {
      setError(`Failed to update ${jobKey}: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(prev => ({ ...prev, [`toggle-${jobKey}`]: false }));
    }
  };

  // Expand/collapse job details
  const handleExpand = (jobKey) => {
    setExpandedJobs(prev => ({
      ...prev,
      [jobKey]: !prev[jobKey],
    }));
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchJobs(), fetchDashboard(), fetchUnifiedHistory(historySearch, historyStatusFilter, historyPage)]);
    setRefreshing(false);
  };

  // Open edit modal for a job
  const handleOpenEditModal = (job) => {
    setEditingJob(job);
  };

  // Save job configuration changes
  const handleSaveJob = async (updates) => {
    if (!editingJob) return;
    setSavingJob(true);
    setError(null);

    try {
      await cronAPI.updateJob(editingJob.job_key, updates);
      setResult({
        success: true,
        message: `Job "${editingJob.display_name}" updated successfully!`,
      });
      setEditingJob(null);
      fetchJobs();
      fetchDashboard();
    } catch (err) {
      setError(`Failed to update job: ${err.response?.data?.message || err.message}`);
    } finally {
      setSavingJob(false);
    }
  };

  // Navigate to job in All Jobs tab (from history "Edit" button)
  const handleNavigateToJob = (jobKey) => {
    setActiveTab('jobs');
    setSelectedCategory('all');
    setExpandedJobs(prev => ({ ...prev, [jobKey]: true }));
    // Scroll to the job card after a brief delay for tab switch
    setTimeout(() => {
      const jobCard = document.querySelector(`[data-job-key="${jobKey}"]`);
      if (jobCard) {
        jobCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // View execution details
  const handleViewExecution = (execution) => {
    setSelectedExecution(execution);
  };

  // Handle search with debounce
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setHistorySearch(searchInput);
      setHistoryPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Filter jobs by category
  const filteredJobs = selectedCategory === 'all'
    ? jobs
    : jobs.filter(j => j.category === selectedCategory);

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-deep-navy mb-2">
              Cron Job Management
            </h1>
            <p className="text-gray-600">
              Database-driven scheduled job configuration and monitoring
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              Environment: <strong className="text-gray-700">{currentEnv.name}</strong>
            </span>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="btn-secondary text-sm py-2 px-3"
            >
              <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Auto-refresh toggle */}
      <div className="card mb-6 bg-blue-50 border border-blue-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClockIcon className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="font-semibold text-blue-900">Auto-Refresh</h3>
              <p className="text-sm text-gray-700">
                Status updates every 15 seconds
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Success Messages */}
      {result && (
        <div className="card mb-6 bg-green-50 border border-green-200">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-green-900">{result.message}</p>
              {result.data?.duration && (
                <p className="text-xs text-green-700 mt-1">
                  Completed in {result.data.duration}ms
                </p>
              )}
            </div>
            <button
              onClick={() => setResult(null)}
              className="text-green-500 hover:text-green-700"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Error Messages */}
      {error && (
        <div className="card mb-6 bg-red-50 border border-red-200">
          <div className="flex items-start gap-3">
            <XCircleIcon className="w-6 h-6 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-red-900">Error</p>
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <XCircleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && (
        <div>
          {/* Health Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <HealthCard
              title="Healthy"
              count={dashboard.summary.healthy}
              color="border-green-200"
              icon={CheckCircleIcon}
              items={dashboard.healthyJobs}
            />
            <HealthCard
              title="Warning"
              count={dashboard.summary.warning}
              color="border-yellow-200"
              icon={ExclamationTriangleIcon}
              items={dashboard.warningJobs}
            />
            <HealthCard
              title="Error"
              count={dashboard.summary.error}
              color="border-red-200"
              icon={XCircleIcon}
              items={dashboard.errorJobs}
            />
            <HealthCard
              title="Disabled"
              count={dashboard.summary.disabled}
              color="border-gray-200"
              icon={PauseIcon}
              items={dashboard.disabledJobs}
            />
          </div>

          {/* Unified Execution History */}
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Execution History</h3>
              <span className="text-xs text-gray-500">
                {unifiedHistoryTotal} total executions
              </span>
            </div>

            {/* Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              {/* Search Input */}
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search job name or error..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="input pl-9 py-2 text-sm w-full"
                />
              </div>

              {/* Status Filter Buttons */}
              <div className="flex gap-1">
                {['all', 'completed', 'failed'].map((status) => (
                  <button
                    key={status}
                    onClick={() => {
                      setHistoryStatusFilter(status);
                      setHistoryPage(1);
                    }}
                    className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                      historyStatusFilter === status
                        ? status === 'completed'
                          ? 'bg-green-600 text-white'
                          : status === 'failed'
                          ? 'bg-red-600 text-white'
                          : 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {status === 'all' ? 'All' : status === 'completed' ? 'Success' : 'Failed'}
                  </button>
                ))}
              </div>
            </div>

            {/* History Table */}
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : unifiedHistory.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Job</th>
                        <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Status</th>
                        <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Duration</th>
                        <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Executed</th>
                        <th className="text-center text-xs font-semibold text-gray-600 px-4 py-3">Edit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {unifiedHistory.map((entry) => (
                        <tr key={entry.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleViewExecution(entry)}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium text-left"
                              title="View execution details"
                            >
                              {entry.display_name || entry.job_key}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[entry.status] || 'bg-gray-100 text-gray-600'}`}>
                              {entry.status === 'completed' ? 'success' : entry.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {entry.duration_ms ? `${entry.duration_ms}ms` : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatRelativeTime(entry.started_at)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => {
                                // Find the full job config and open edit modal
                                const job = jobs.find(j => j.job_key === entry.job_key);
                                if (job) handleOpenEditModal(job);
                              }}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Edit job settings"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {unifiedHistoryTotal > HISTORY_PAGE_SIZE && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                    <span className="text-xs text-gray-500">
                      Showing {(historyPage - 1) * HISTORY_PAGE_SIZE + 1} - {Math.min(historyPage * HISTORY_PAGE_SIZE, unifiedHistoryTotal)} of {unifiedHistoryTotal}
                    </span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                        disabled={historyPage === 1}
                        className="btn-secondary text-xs py-1 px-3 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setHistoryPage(p => p + 1)}
                        disabled={historyPage * HISTORY_PAGE_SIZE >= unifiedHistoryTotal}
                        className="btn-secondary text-xs py-1 px-3 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                {historySearch || historyStatusFilter !== 'all'
                  ? 'No executions match your filters'
                  : 'No execution history yet'}
              </div>
            )}
          </div>

          {/* Recent Failures */}
          {dashboard.recentFailures && dashboard.recentFailures.length > 0 && (
            <div className="card bg-red-50 border border-red-200">
              <h3 className="text-lg font-semibold text-red-900 mb-4">
                Recent Failures (Last 24h)
              </h3>
              <div className="space-y-2">
                {dashboard.recentFailures.map((failure, i) => (
                  <div key={i} className="bg-white p-3 rounded-lg border border-red-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{failure.display_name}</p>
                        <p className="text-xs text-gray-500">
                          {formatRelativeTime(failure.started_at)}
                        </p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                        {failure.category}
                      </span>
                    </div>
                    {failure.error_message && (
                      <p className="text-xs text-red-600 mt-2 truncate">
                        {failure.error_message}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* All Jobs Tab */}
      {activeTab === 'jobs' && (
        <div>
          {/* Category Filter */}
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm text-gray-600">Filter by category:</span>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="input py-1 px-3 text-sm"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-400">
              ({filteredJobs.length} jobs)
            </span>
          </div>

          {/* Job Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredJobs.map((job) => (
              <div key={job.job_key} data-job-key={job.job_key}>
                <JobCard
                  job={job}
                  onTrigger={handleTrigger}
                  onToggle={handleToggle}
                  onExpand={handleExpand}
                  onEdit={handleOpenEditModal}
                  isExpanded={expandedJobs[job.job_key]}
                  loading={loading}
                />
              </div>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No jobs found in this category
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          {/* Job Selector */}
          <div className="mb-4 flex items-center gap-4">
            <span className="text-sm text-gray-600">Select job:</span>
            <select
              value={selectedJobHistory || ''}
              onChange={(e) => fetchJobHistory(e.target.value)}
              className="input py-1 px-3 text-sm"
            >
              <option value="">Choose a job...</option>
              {jobs.map((job) => (
                <option key={job.job_key} value={job.job_key}>
                  {job.display_name}
                </option>
              ))}
            </select>
          </div>

          {/* History Table */}
          {history.length > 0 ? (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Started</th>
                    <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Status</th>
                    <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Duration</th>
                    <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Triggered By</th>
                    <th className="text-left text-xs font-semibold text-gray-600 px-4 py-3">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {history.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(entry.started_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[entry.status] || 'bg-gray-100 text-gray-600'}`}>
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {entry.duration_ms ? `${entry.duration_ms}ms` : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {entry.triggered_by}
                      </td>
                      <td className="px-4 py-3 text-xs text-red-600 max-w-xs truncate">
                        {entry.error_message || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : selectedJobHistory ? (
            <div className="text-center py-12 text-gray-500">
              No history found for this job
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              Select a job to view its execution history
            </div>
          )}
        </div>
      )}

      {/* API Response */}
      {result?.data && <JsonViewer data={result.data} title="API Response" />}

      {/* Documentation */}
      <div className="card bg-gray-50 mt-6">
        <h3 className="font-semibold text-gray-800 mb-3">Database-Driven Cron Management</h3>
        <ul className="text-sm text-gray-700 space-y-2">
          <li>
            <strong>Configuration:</strong> Jobs are stored in <code>cron_job_config</code> table
          </li>
          <li>
            <strong>History:</strong> Execution history tracked in <code>cron_job_history</code> table
          </li>
          <li>
            <strong>Live Updates:</strong> Schedule changes take effect without service restart
          </li>
          <li>
            <strong>Authentication:</strong> Uses your verified Google Fold session
          </li>
          <li>
            <strong>Audit Trail:</strong> All manual triggers logged to audit_logs
          </li>
        </ul>
      </div>

      {/* API Endpoints */}
      <div className="card bg-blue-50 border border-blue-200 mt-6">
        <h3 className="font-semibold text-blue-900 mb-3">API Endpoints</h3>
        <div className="space-y-2 text-sm font-mono text-blue-800">
          <div>
            <span className="text-blue-600 font-semibold">GET</span>{' '}
            /api/admin/cron/jobs
          </div>
          <div>
            <span className="text-blue-600 font-semibold">GET</span>{' '}
            /api/admin/cron/jobs/:jobKey
          </div>
          <div>
            <span className="text-yellow-600 font-semibold">PATCH</span>{' '}
            /api/admin/cron/jobs/:jobKey
          </div>
          <div>
            <span className="text-green-600 font-semibold">POST</span>{' '}
            /api/admin/cron/jobs/:jobKey/trigger
          </div>
          <div>
            <span className="text-blue-600 font-semibold">GET</span>{' '}
            /api/admin/cron/jobs/:jobKey/history
          </div>
          <div>
            <span className="text-blue-600 font-semibold">GET</span>{' '}
            /api/admin/cron/history
            <span className="text-xs text-gray-500 ml-2">(unified, searchable)</span>
          </div>
          <div>
            <span className="text-blue-600 font-semibold">GET</span>{' '}
            /api/admin/cron/dashboard
          </div>
        </div>
      </div>

      {/* Edit Job Modal */}
      {editingJob && (
        <EditJobModal
          job={editingJob}
          onSave={handleSaveJob}
          onClose={() => setEditingJob(null)}
          saving={savingJob}
        />
      )}

      {/* Execution Detail Modal */}
      {selectedExecution && (
        <ExecutionDetailModal
          execution={selectedExecution}
          onClose={() => setSelectedExecution(null)}
        />
      )}
    </div>
  );
}
